<script setup>
/**
 * Shortcuts Modal Component
 * Modal for selecting shortcuts to add to the desktop
 */

import { computed } from 'vue'
import { ICON_SVG_PATHS } from '@/core/config/desktopShortcuts'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  availableShortcuts: {
    type: Object,
    default: () => ({}),
  },
  addedShortcutIds: {
    type: Set,
    default: () => new Set(),
  },
})

const emit = defineEmits(['close', 'select'])

function isAdded(shortcutId) {
  return props.addedShortcutIds.has(shortcutId)
}

function getIconPath(iconName) {
  return ICON_SVG_PATHS[iconName] || ICON_SVG_PATHS['portal']
}

function selectShortcut(shortcut) {
  if (!isAdded(shortcut.id)) {
    emit('select', shortcut)
  }
}

function handleOverlayClick(e) {
  if (e.target === e.currentTarget) {
    emit('close')
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="isOpen" class="modal-overlay" @click="handleOverlayClick">
        <div class="modal-content">
          <div class="modal-header">
            <h2>בחירת קיצורי דרך</h2>
            <button class="close-btn" @click="$emit('close')">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                />
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <p class="modal-description">
              בחר קיצורי דרך להוספה לשולחן העבודה. לאחר הבחירה תוכל לגרור ולמקם אותם איפה שתרצה.
            </p>

            <template v-for="(category, key) in availableShortcuts" :key="key">
              <div class="category-section">
                <h3 class="category-title">{{ category.label }}</h3>
                <div class="shortcuts-grid">
                  <button
                    v-for="shortcut in category.items"
                    :key="shortcut.id"
                    class="shortcut-item"
                    :class="{ added: isAdded(shortcut.id) }"
                    :disabled="isAdded(shortcut.id)"
                    @click="selectShortcut(shortcut)"
                  >
                    <div class="shortcut-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path :d="getIconPath(shortcut.icon)" />
                      </svg>
                    </div>
                    <span class="shortcut-label">{{ shortcut.label }}</span>
                    <span v-if="isAdded(shortcut.id)" class="added-badge">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
            </template>

            <div v-if="Object.keys(availableShortcuts).length === 0" class="empty-state">
              <p>אין קיצורי דרך זמינים</p>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: linear-gradient(180deg, #1e1e2e 0%, #171723 100%);
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid #2d2d4a;
}

/* Full screen on mobile */
@media (max-width: 640px) {
  .modal-overlay {
    padding: 0;
  }

  .modal-content {
    max-width: 100%;
    max-height: 100%;
    height: 100%;
    border-radius: 0;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #2d2d4a;
  background: rgba(0, 0, 0, 0.2);
}

.modal-header h2 {
  color: white;
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.close-btn svg {
  width: 24px;
  height: 24px;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.modal-description {
  color: #aaa;
  font-size: 0.9rem;
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
}

.category-section {
  margin-bottom: 1.75rem;
}

.category-section:last-child {
  margin-bottom: 0;
}

.category-title {
  color: #888;
  font-size: 0.8rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.shortcuts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 0.75rem;
}

@media (max-width: 640px) {
  .shortcuts-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.6rem;
  }
}

.shortcut-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0.75rem;
  background: #2a2a40;
  border: 2px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.shortcut-item:hover:not(:disabled) {
  background: #3a3a55;
  border-color: #4a9eff;
  transform: translateY(-2px);
}

.shortcut-item:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.shortcut-item.added {
  background: #1d3d1d;
  border-color: #2d5d2d;
}

.shortcut-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 12px;
  color: white;
  box-shadow: 0 3px 10px rgba(59, 130, 246, 0.3);
}

.shortcut-item.added .shortcut-icon {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 3px 10px rgba(34, 197, 94, 0.3);
}

.shortcut-icon svg {
  width: 22px;
  height: 22px;
}

.shortcut-label {
  color: white;
  font-size: 0.8rem;
  text-align: center;
  line-height: 1.2;
}

.added-badge {
  position: absolute;
  top: 0.35rem;
  left: 0.35rem;
  background: #22c55e;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.added-badge svg {
  width: 14px;
  height: 14px;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #666;
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.95) translateY(10px);
}
</style>
